import { __decorate, __param, __metadata } from 'tslib';
import * as i0 from '@angular/core';
import { EventEmitter, Optional, Inject, ElementRef, ChangeDetectorRef, Component, ViewChild, ChangeDetectionStrategy, TemplateRef, Injectable, SkipSelf, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import { ESCAPE, getFocusableBoundaryElements, focusTrap } from '@anthem/uxd/util';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import * as i2 from '@angular/cdk/portal';
import { BasePortalOutlet, CdkPortalOutlet, ComponentPortal, PortalInjector, TemplatePortal, PortalModule } from '@angular/cdk/portal';
import { state, style, transition, animate, trigger } from '@angular/animations';
import * as i1$1 from '@anthem/uxd/overlay';
import { OverlayContainerService, OverlayService, OverlayModule } from '@anthem/uxd/overlay';

let uniqueId = 0;
class ModalRef {
    get id() {
        return this._id;
    }
    constructor(_overlayRef, _containerInstance, id = `${uniqueId++}`) {
        this._overlayRef = _overlayRef;
        this._containerInstance = _containerInstance;
        this.onClose = new Subject();
        this.onDismiss = new Subject();
        this._id = id;
        this._overlayRef.backdropClick().subscribe((event) => {
            this.dismiss();
        });
        if (this._containerInstance && this._containerInstance._elementRef) {
            const containerElement = this._containerInstance._elementRef.nativeElement;
            containerElement.addEventListener('keydown', this._handleKeydown.bind(this));
        }
    }
    close(result) {
        if (this._overlayRef && this._containerInstance) {
            this._containerInstance._startExitAnimation();
            this._containerInstance._animationStateChange
                .pipe(filter((event) => event.phaseName === 'done'), take(1))
                .subscribe(() => {
                this._overlayRef.detach();
                this._containerInstance._remove();
            });
            this.onClose.next(result);
        }
    }
    dismiss(reason) {
        this._containerInstance._startExitAnimation();
        this._containerInstance._animationStateChange
            .pipe(filter((event) => event.phaseName === 'done'), take(1))
            .subscribe(() => {
            this._overlayRef.detach();
            this._containerInstance._remove();
        });
        this.onDismiss.next();
    }
    _handleKeydown(event) {
        const keyCode = event.keyCode || event.which;
        if (keyCode === ESCAPE) {
            this.dismiss();
        }
    }
}
let BaseModalContainer = class BaseModalContainer extends BasePortalOutlet {
    constructor(_elementRef, _changeDetectorRef, _document) {
        super();
        this._elementRef = _elementRef;
        this._changeDetectorRef = _changeDetectorRef;
        this._document = _document;
        this._animationStateChange = new EventEmitter();
        this._activeModalCmpChanges$ = new Subject();
    }
    _createFocusTrap() {
        /** Determine the first and last focusable elements within a modal.
         * By default, focus should be set on the first element automatically. */
        const container = this._elementRef && this._elementRef.nativeElement;
        const [first, last] = getFocusableBoundaryElements(container);
        first.focus();
        /** When the view has completed rendering, create a
         * focus trap, which will terminate whenever
         * `_activeModalCmpChanges$` is updated again. */
        this._trapFocus(this._activeModalCmpChanges$);
        /**
         * When the modal content has been destroyed,
         * remove the focus trap by emitting to `_activeModalCmpChanges$` */
        this._portalOutlet.attachedRef.onDestroy(() => {
            this._activeModalCmpChanges$.next(false);
        });
    }
    _initialize(portalOutlet, _startEnterAnimation) {
        this._portalOutlet = portalOutlet;
        this._startEnterAnimation = _startEnterAnimation;
    }
    /**
     * Attach a ComponentPortal as content to the modal container;
     * With portals, the outlet is a 'placeholder' where a component or template
     * may be inserted; the portal itself is the content that should be inserted.
     * @see https://github.com/angular/material2/tree/master/src/cdk/portal
     *
     * @param portal to be attached to the modal body
     */
    attachComponentPortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            return null;
        }
        this._savePreviouslyFocusedElement();
        const componentRef = this._portalOutlet.attachComponentPortal(portal);
        // this._startEnterAnimation();
        return componentRef;
    }
    /**
     * Attach a TemplatePortal as content to the modal container.
     * With portals, the outlet is a 'placeholder' where a component or template
     * may be inserted; the portal itself is the content that should be inserted.
     * @see https://github.com/angular/material2/tree/master/src/cdk/portal
     *
     * @param portal to be attached to the modal body
     */
    attachTemplatePortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            return null;
        }
        this._savePreviouslyFocusedElement();
        const viewRef = this._portalOutlet.attachTemplatePortal(portal);
        // this._startEnterAnimation();
        return viewRef;
    }
    /**
     * Saves a reference to the element having focus just prior
     * to the modal being opened.
     * When the modal is closed, focus should return to this element.
     */
    _savePreviouslyFocusedElement() {
        if (!this._document) {
            return;
        }
        this._elementFocusedBefore = this._document.activeElement;
    }
    /**
     * Creates a focus-trap around the modal, ensuring a user cannot
     * TAB or SHIFT+TAB out of the open modal.
     */
    _trapFocus(onStop$) {
        focusTrap(this._elementRef.nativeElement, onStop$);
    }
    _restoreFocus() {
        const elementToFocus = this._elementFocusedBefore;
        if (elementToFocus && typeof elementToFocus.focus === 'function') {
            elementToFocus.focus();
        }
    }
    _remove() {
        this._portalOutlet.detach();
    }
    _startEnterAnimation(callback) {
        this._animationState = 'enter';
        this._changeDetectorRef.markForCheck();
        this._activeModalCmpChanges$.next(true);
        if (callback && typeof callback === 'function') {
            callback();
        }
    }
    _startExitAnimation(callback) {
        this._animationState = 'exit';
        this._changeDetectorRef.markForCheck();
        this._activeModalCmpChanges$.next(true);
        if (callback && typeof callback === 'function') {
            callback();
        }
    }
};
BaseModalContainer = __decorate([
    __param(2, Optional()),
    __param(2, Inject(DOCUMENT)),
    __metadata("design:paramtypes", [ElementRef,
        ChangeDetectorRef, Object])
], BaseModalContainer);

const dialogModalAnimation = [
    state('void, exit', style({ opacity: 0, transform: 'translateY(1000px) scale(0.7)' })),
    state('enter', style({ transform: 'none' })),
    transition('* => enter', animate('180ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'none', opacity: 1 }))),
    transition('* => void, * => exit', animate('300ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateY(-500px) scale(0.8)' })))
];
const slideInAnimation = [
    // LEFT ANIMATIONS
    transition('* => enter-left', [style({ opacity: 0, transform: 'translateX(-100%)' }), animate('500ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateX(0)' }))]),
    transition('* => exit-left', animate('500ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateX(-100%)' }))),
    // RIGHT ANIMATIONS
    transition('* => enter-right', [style({ opacity: 0, transform: 'translateX(100%)' }), animate('500ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateX(0)' }))]),
    transition('* => exit-right', animate('500ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateX(100%)' }))),
    // TOP ANIMATIONS
    transition('* => enter-top', [style({ opacity: 0, transform: 'translateY(-100%)' }), animate('500ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateY(0)' }))]),
    transition('* => exit-top', animate('500ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateY(-100%)' }))),
    // BOTTOM ANIMATIONS
    transition('* => enter-bottom', [style({ opacity: 0, transform: 'translateY(100%)' }), animate('500ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateY(0)' }))]),
    transition('* => exit-bottom', animate('1000ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateY(100%)' })))
];
const modalAnimations = {
    slideDown: trigger('slideDown', dialogModalAnimation)
};
const panelAnimations = {
    slideIn: trigger('slideIn', slideInAnimation)
};

function SidePanelContainerComponent_ng_template_2_Template(rf, ctx) { }
class SidePanelContainerComponent extends BaseModalContainer {
    constructor(_elementRef, _changeDetectorRef, _document, _renderer) {
        super(_elementRef, _changeDetectorRef, _document);
        this._elementRef = _elementRef;
        this._renderer = _renderer;
    }
    ngAfterViewInit() {
        this._initialize(this._portalOutlet, this._startEnterAnimation);
        this._createFocusTrap();
    }
    ngOnDestroy() {
        this._activeModalCmpChanges$.complete();
        this._restoreFocus();
    }
    _onAnimationStart(event) {
        this._animationStateChange.emit(event);
    }
    _setAnimationDirection(direction) {
        this._animationDirection = direction;
    }
    _onAnimationDone(event) {
        if (event.toState.indexOf('enter') + 1) {
            this._trapFocus(this._activeModalCmpChanges$);
        }
        else if (event.toState.indexOf('exit') + 1) {
            this._restoreFocus();
        }
        this._animationStateChange.emit(event);
    }
    _startEnterAnimation() {
        if (this._animationDirection !== undefined) {
            this._animationState = 'enter-' + this._animationDirection;
            this._renderer.addClass(this._document.body, 'ant-scroll-block');
            this._changeDetectorRef.markForCheck();
            this._activeModalCmpChanges$.next(true);
        }
    }
    _startExitAnimation() {
        this._animationState = 'exit-' + this._animationDirection;
        this._renderer.removeClass(this._document.body, 'ant-scroll-block');
        this._changeDetectorRef.markForCheck();
        this._activeModalCmpChanges$.next(true);
    }
    static { this.ɵfac = function SidePanelContainerComponent_Factory(t) { return new (t || SidePanelContainerComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(DOCUMENT, 8), i0.ɵɵdirectiveInject(i0.Renderer2)); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: SidePanelContainerComponent, selectors: [["side-panel-container"]], viewQuery: function SidePanelContainerComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(CdkPortalOutlet, 7);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._portalOutlet = _t.first);
        } }, hostAttrs: ["tabindex", "0"], features: [i0.ɵɵInheritDefinitionFeature], decls: 3, vars: 2, consts: [[1, "modal-container", "slide-in", 3, "ngClass"], [1, "modal-panel"], ["cdkPortalOutlet", ""]], template: function SidePanelContainerComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "div", 1);
            i0.ɵɵlistener("@slideIn.start", function SidePanelContainerComponent_Template_div_animation_slideIn_start_1_listener($event) { return ctx._onAnimationStart($event); })("@slideIn.done", function SidePanelContainerComponent_Template_div_animation_slideIn_done_1_listener($event) { return ctx._onAnimationDone($event); });
            i0.ɵɵtemplate(2, SidePanelContainerComponent_ng_template_2_Template, 0, 0, "ng-template", 2);
            i0.ɵɵelementEnd()();
        } if (rf & 2) {
            i0.ɵɵproperty("ngClass", ctx._animationDirection);
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("@slideIn", ctx._animationState);
        } }, dependencies: [i1.NgClass, i2.CdkPortalOutlet], encapsulation: 2, data: { animation: [panelAnimations.slideIn] } }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SidePanelContainerComponent, [{
        type: Component,
        args: [{ selector: 'side-panel-container', host: {
                    tabindex: '0'
                }, animations: [panelAnimations.slideIn], template: "<div class=\"modal-container slide-in\" [ngClass]=\"_animationDirection\">\n  <div class=\"modal-panel\"\n    [@slideIn]=\"_animationState\"\n    (@slideIn.start)=\"_onAnimationStart($event)\"\n    (@slideIn.done)=\"_onAnimationDone($event)\">\n    <ng-template cdkPortalOutlet></ng-template>\n  </div>\n</div>" }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: i0.Renderer2 }]; }, { _portalOutlet: [{
            type: ViewChild,
            args: [CdkPortalOutlet, { static: true }]
        }] }); })();

function ModalContainerComponent_ng_template_2_Template(rf, ctx) { }
class ModalContainerComponent extends BaseModalContainer {
    constructor(_elementRef, _changeDetectorRef, _document, _renderer) {
        super(_elementRef, _changeDetectorRef, _document);
        this._elementRef = _elementRef;
        this._renderer = _renderer;
        this._animationState = 'void';
        this._animationStateChange = new EventEmitter();
    }
    ngAfterViewInit() {
        /** Initialize just sets the parent's portaloutlet property to this view's portaloutlet */
        this._initialize(this._portalOutlet, this._startEnterAnimation);
        /** Creates focus trap right away (references parent's focustrap) */
        this._createFocusTrap();
    }
    ngOnDestroy() {
        this._activeModalCmpChanges$.complete();
        this._restoreFocus();
    }
    _onAnimationStart(event) {
        this._animationStateChange.emit(event);
    }
    _onAnimationDone(event) {
        if (event.toState === 'enter') {
            this._trapFocus(this._activeModalCmpChanges$);
        }
        else if (event.toState === 'exit') {
            this._restoreFocus();
        }
        this._animationStateChange.emit(event);
    }
    _startEnterAnimation() {
        this._animationState = 'enter';
        this._renderer.addClass(this._document.body, 'ant-scroll-block');
        this._changeDetectorRef.markForCheck();
        this._activeModalCmpChanges$.next(true);
    }
    _startExitAnimation() {
        this._animationState = 'exit';
        this._renderer.removeClass(this._document.body, 'ant-scroll-block');
        this._changeDetectorRef.markForCheck();
        this._activeModalCmpChanges$.next(true);
    }
    static { this.ɵfac = function ModalContainerComponent_Factory(t) { return new (t || ModalContainerComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(DOCUMENT, 8), i0.ɵɵdirectiveInject(i0.Renderer2)); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ModalContainerComponent, selectors: [["uxd-modal-container"]], viewQuery: function ModalContainerComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(CdkPortalOutlet, 7);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._portalOutlet = _t.first);
        } }, hostAttrs: ["tabindex", "0"], features: [i0.ɵɵInheritDefinitionFeature], decls: 3, vars: 1, consts: [[1, "modal-container", "dialog"], [1, "modal-panel"], ["cdkPortalOutlet", ""]], template: function ModalContainerComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "div", 1);
            i0.ɵɵlistener("@slideDown.start", function ModalContainerComponent_Template_div_animation_slideDown_start_1_listener($event) { return ctx._onAnimationStart($event); })("@slideDown.done", function ModalContainerComponent_Template_div_animation_slideDown_done_1_listener($event) { return ctx._onAnimationDone($event); });
            i0.ɵɵtemplate(2, ModalContainerComponent_ng_template_2_Template, 0, 0, "ng-template", 2);
            i0.ɵɵelementEnd()();
        } if (rf & 2) {
            i0.ɵɵadvance(1);
            i0.ɵɵproperty("@slideDown", ctx._animationState);
        } }, dependencies: [i2.CdkPortalOutlet], encapsulation: 2, data: { animation: [modalAnimations.slideDown] } }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ModalContainerComponent, [{
        type: Component,
        args: [{ selector: 'uxd-modal-container', host: {
                    tabindex: '0'
                }, changeDetection: ChangeDetectionStrategy.Default, animations: [modalAnimations.slideDown], template: "<div class=\"modal-container dialog\">\n  <div class=\"modal-panel\"\n    [@slideDown]=\"_animationState\"\n    (@slideDown.start)=\"_onAnimationStart($event)\"\n    (@slideDown.done)=\"_onAnimationDone($event)\">\n    <ng-template cdkPortalOutlet></ng-template>\n  </div>\n</div>\n" }]
    }], function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: i0.Renderer2 }]; }, { _portalOutlet: [{
            type: ViewChild,
            args: [CdkPortalOutlet, { static: true }]
        }] }); })();

class Modal {
    constructor(_injector, _overlay, _parentModal) {
        this._injector = _injector;
        this._overlay = _overlay;
        this._parentModal = _parentModal;
        this._openModals = [];
    }
    open(compOrTempRef, backdropClass = '') {
        /** Create overlay ref and calls pattern library overlay service
         *  See overlay.service.ts -> create()
         */
        const overlayRef = this._overlay.create({ hasBackdrop: true, backdropClass: backdropClass });
        /** Create component view container and get instance */
        const modalContainer = this._attachModalContainer(overlayRef);
        /** Create new modal with given ng-content transclusion
         * and attach component or template portal.
         */
        const modalRef = this._attachModalContent(compOrTempRef, modalContainer, overlayRef);
        /** Starts animation on view container, essentially opening it in the view. */
        modalContainer._startEnterAnimation();
        /** Adds modalref to list of open modals. */
        this._openModals.push(modalRef);
        return modalRef;
    }
    _createOverylay(containerType, backdropClass) {
        return this._overlay.create({
            hasBackdrop: true,
            backdropClass: backdropClass
        });
    }
    _attachModalContainer(overlay) {
        const modalContainerPortal = new ComponentPortal(ModalContainerComponent, null, null);
        const modalContainerRef = overlay.attach(modalContainerPortal);
        return modalContainerRef.instance;
    }
    _createInjector(modalRef, modalContainer) {
        // The MatDialogContainer is injected in the portal as the MatDialogContainer and the dialog's
        // content are created out of the same ViewContainerRef and as such, are siblings for injector
        // purposes. To allow the hierarchy that is expected, the MatDialogContainer is explicitly
        // added to the injection tokens.
        const injectionTokens = new WeakMap([[ModalContainerComponent, modalContainer], [ModalRef, modalRef]]);
        return new PortalInjector(this._injector, injectionTokens);
    }
    _attachModalContent(compOrTempRef, modalContainer, overlay) {
        const modalRef = new ModalRef(overlay, modalContainer);
        if (compOrTempRef instanceof TemplateRef) {
            modalContainer.attachTemplatePortal(new TemplatePortal(compOrTempRef, null, { $implicit: null, modalRef }));
        }
        else {
            const injector = this._createInjector(modalRef, modalContainer);
            const modal = modalContainer.attachComponentPortal(new ComponentPortal(compOrTempRef, null, injector));
            modalRef.componentInstance = modal.instance;
        }
        return modalRef;
    }
    static { this.ɵfac = function Modal_Factory(t) { return new (t || Modal)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(i1$1.OverlayService), i0.ɵɵinject(Modal, 12)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: Modal, factory: Modal.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(Modal, [{
        type: Injectable
    }], function () { return [{ type: i0.Injector }, { type: i1$1.OverlayService }, { type: Modal, decorators: [{
                type: Optional
            }, {
                type: SkipSelf
            }] }]; }, null); })();

class ModalModule {
    static forRoot() {
        return {
            ngModule: ModalModule,
            providers: [Modal]
        };
    }
    static { this.ɵfac = function ModalModule_Factory(t) { return new (t || ModalModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: ModalModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [OverlayContainerService, OverlayService], imports: [CommonModule, PortalModule, OverlayModule] }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ModalModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, PortalModule, OverlayModule],
                declarations: [ModalContainerComponent, SidePanelContainerComponent],
                exports: [ModalContainerComponent, SidePanelContainerComponent],
                providers: [OverlayContainerService, OverlayService]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(ModalModule, { declarations: [ModalContainerComponent, SidePanelContainerComponent], imports: [CommonModule, PortalModule, OverlayModule], exports: [ModalContainerComponent, SidePanelContainerComponent] }); })();

class SidePanel {
    constructor(_injector, _overlay, _parentPanel) {
        this._injector = _injector;
        this._overlay = _overlay;
        this._parentPanel = _parentPanel;
        this._openModals = [];
    }
    open(direction, compOrTempRef) {
        const overlayRef = this._createOverylay(new ComponentPortal(SidePanelContainerComponent));
        const sidePanelContainer = this._attachModalContainer(overlayRef);
        const modalRef = this._attachModalContent(compOrTempRef, sidePanelContainer, overlayRef);
        sidePanelContainer._setAnimationDirection(direction);
        sidePanelContainer._startEnterAnimation();
        this._openModals.push(modalRef);
        return modalRef;
    }
    _createOverylay(containerType) {
        // backdropClass prevents this modal from closing on background click
        return this._overlay.create({ hasBackdrop: true, backdropClass: 'disable-backdrop-click' });
    }
    _attachModalContainer(overlay) {
        const modalContainerPortal = new ComponentPortal(SidePanelContainerComponent, null, null);
        const modalContainerRef = overlay.attach(modalContainerPortal);
        return modalContainerRef.instance;
    }
    _createInjector(modalRef, modalContainer) {
        // The MatDialogContainer is injected in the portal as the MatDialogContainer and the dialog's
        // content are created out of the same ViewContainerRef and as such, are siblings for injector
        // purposes. To allow the hierarchy that is expected, the MatDialogContainer is explicitly
        // added to the injection tokens.
        const injectionTokens = new WeakMap([[SidePanelContainerComponent, modalContainer], [ModalRef, modalRef]]);
        return new PortalInjector(this._injector, injectionTokens);
    }
    _attachModalContent(compOrTempRef, modalContainer, overlay) {
        const modalRef = new ModalRef(overlay, modalContainer);
        if (compOrTempRef instanceof TemplateRef) {
            modalContainer.attachTemplatePortal(new TemplatePortal(compOrTempRef, null, { $implicit: null, modalRef }));
        }
        else {
            const injector = this._createInjector(modalRef, modalContainer);
            const modal = modalContainer.attachComponentPortal(new ComponentPortal(compOrTempRef, null, injector));
            modalRef.componentInstance = modal.instance;
        }
        return modalRef;
    }
    static { this.ɵfac = function SidePanel_Factory(t) { return new (t || SidePanel)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(i1$1.OverlayService), i0.ɵɵinject(SidePanel, 12)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: SidePanel, factory: SidePanel.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SidePanel, [{
        type: Injectable
    }], function () { return [{ type: i0.Injector }, { type: i1$1.OverlayService }, { type: SidePanel, decorators: [{
                type: Optional
            }, {
                type: SkipSelf
            }] }]; }, null); })();

/**
 * Generated bundle index. Do not edit.
 */

export { BaseModalContainer, Modal, ModalContainerComponent, ModalModule, ModalRef, SidePanel, SidePanelContainerComponent, modalAnimations, panelAnimations };
